// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Users, 
  DollarSign, 
  Target, 
  TrendingUp, 
  Plus, 
  Calendar,
  Phone,
  Mail,
  MessageSquare,
  Clock,
  Eye,
  AlertTriangle
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

interface DashboardStats {
  totalLeads: number;
  pipelineValue: number;
  monthlyCloses: number;
  activityScore: number;
}

interface RecentActivity {
  id: string;
  type: 'call' | 'email' | 'meeting' | 'demo';
  description: string;
  time: string;
  leadName: string;
}

interface HotLead {
  id: string;
  name: string;
  company: string;
  value: number;
  status: string;
  lastContact: string;
  nextFollowUp: string;
}

const SalesDashboard: React.FC = () => {
  const { profile } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    totalLeads: 0,
    pipelineValue: 0,
    monthlyCloses: 0,
    activityScore: 0
  });
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([]);
  const [hotLeads, setHotLeads] = useState<HotLead[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (profile?.id) {
      loadDashboardData();
    }
  }, [profile?.id]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Load stats
      const { data: leads, error: leadsError } = await supabase
        .from('sales_leads')
        .select('*')
        .eq('sales_rep_id', profile?.id);

      if (leadsError) throw leadsError;

      // Load recent activities
      const { data: activities, error: activitiesError } = await supabase
        .from('sales_activities')
        .select(`
          *,
          sales_leads!inner(name, company)
        `)
        .eq('sales_rep_id', profile?.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (activitiesError) throw activitiesError;

      // Calculate stats
      const totalLeads = leads?.length || 0;
      const pipelineValue = leads?.reduce((sum, lead) => sum + (lead.value || 0), 0) || 0;
      const monthlyCloses = leads?.filter(lead => {
        const leadDate = new Date(lead.created_at);
        const now = new Date();
        return leadDate.getMonth() === now.getMonth() && 
               leadDate.getFullYear() === now.getFullYear() &&
               lead.status === 'closed_won';
      }).length || 0;

      const activityScore = activities?.length ? Math.min(activities.length * 10, 100) : 0;

      setStats({
        totalLeads,
        pipelineValue,
        monthlyCloses,
        activityScore
      });

      // Format recent activities
      const formattedActivities: RecentActivity[] = activities?.map(activity => ({
        id: activity.id,
        type: activity.activity_type,
        description: activity.subject || activity.description || 'No description',
        time: new Date(activity.created_at).toLocaleTimeString(),
        leadName: activity.sales_leads.name
      })) || [];

      setRecentActivities(formattedActivities);

      // Format hot leads (qualified and hot leads)
      const hotLeadsData: HotLead[] = leads?.filter(lead => 
        ['hot', 'qualified'].includes(lead.status)
      ).map(lead => ({
        id: lead.id,
        name: lead.name,
        company: lead.company || 'No company',
        value: lead.value || 0,
        status: lead.status,
        lastContact: lead.last_contact || 'Never',
        nextFollowUp: lead.next_follow_up || 'Not set'
      })) || [];

      setHotLeads(hotLeadsData);

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'call':
        return <Phone className="h-4 w-4" />;
      case 'email':
        return <Mail className="h-4 w-4" />;
      case 'meeting':
        return <MessageSquare className="h-4 w-4" />;
      case 'demo':
        return <Eye className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'hot':
        return 'bg-red-100 text-red-800';
      case 'qualified':
        return 'bg-orange-100 text-orange-800';
      case 'warm':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>;
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Welcome back, {profile?.name}!
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Here's what's happening with your sales pipeline today.
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Add Lead
          </Button>
          <Button variant="outline" size="sm">
            <Calendar className="h-4 w-4 mr-2" />
            Schedule Demo
          </Button>
          <Button variant="outline" size="sm">
            <Eye className="h-4 w-4 mr-2" />
            View Calendar
          </Button>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Leads</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalLeads}</div>
            <p className="text-xs text-muted-foreground">
              +12% from last month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pipeline Value</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats.pipelineValue)}</div>
            <p className="text-xs text-muted-foreground">
              +20% from last month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This Month's Closes</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.monthlyCloses}</div>
            <p className="text-xs text-muted-foreground">
              Goal: 5 closes
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Activity Score</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.activityScore}/100</div>
            <p className="text-xs text-muted-foreground">
              Based on daily activities
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity Feed */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
            <CardDescription>Your latest sales activities</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentActivities.length > 0 ? (
                recentActivities.map((activity) => (
                  <div key={activity.id} className="flex items-center gap-3 p-3 rounded-lg border">
                    <div className="flex-shrink-0 p-2 rounded-full bg-primary/10">
                      {getActivityIcon(activity.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {activity.description}
                      </p>
                      <p className="text-xs text-gray-500">
                        {activity.leadName} • {activity.time}
                      </p>
                    </div>
                    <Badge variant="outline" className="capitalize">
                      {activity.type}
                    </Badge>
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-500 py-8">
                  No recent activities. Start by adding some leads!
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Hot Leads */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Hot Leads Requiring Attention
            </CardTitle>
            <CardDescription>Follow up with these prospects immediately</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {hotLeads.length > 0 ? (
                hotLeads.map((lead) => (
                  <div key={lead.id} className="flex items-center justify-between p-3 rounded-lg border">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-medium">{lead.name}</h4>
                        <Badge className={getStatusColor(lead.status)}>
                          {lead.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500">{lead.company}</p>
                      <p className="text-xs text-gray-500">
                        Value: {formatCurrency(lead.value)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">
                        Last: {lead.lastContact}
                      </p>
                      <p className="text-xs text-red-600 font-medium">
                        Next: {lead.nextFollowUp}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-500 py-8">
                  No hot leads at the moment. Great job staying on top of your pipeline!
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Target Segment Quick Stats */}
      <Card>
        <CardHeader>
          <CardTitle>Target Segments Overview</CardTitle>
          <CardDescription>Quick stats for each prospect segment</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { name: 'Affiliates', pending: 5, active: 23, revenue: 15000 },
              { name: 'White Label', demos: 3, trials: 7, implementations: 2 },
              { name: 'Teams', registrations: 45, events: 12, renewals: 8 },
              { name: 'Partners', proposals: 4, active: 6, revenue: 25000 },
              { name: 'Advertisers', proposals: 2, campaigns: 5, spend: 35000 },
              { name: 'College Coaches', signups: 67, pending: 12, upgrades: 15 }
            ].map((segment, index) => (
              <div key={index} className="p-4 rounded-lg border bg-gray-50 dark:bg-gray-800">
                <h4 className="font-medium text-sm mb-2">{segment.name}</h4>
                <div className="space-y-1 text-xs text-gray-600 dark:text-gray-400">
                  {Object.entries(segment).map(([key, value]) => 
                    key !== 'name' && (
                      <div key={key} className="flex justify-between">
                        <span className="capitalize">{key.replace('_', ' ')}:</span>
                        <span className="font-medium">
                          {typeof value === 'number' && (key.includes('revenue') || key.includes('spend')) 
                            ? formatCurrency(value as number) 
                            : value}
                        </span>
                      </div>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SalesDashboard;